import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@chakra-ui/react';
import { Navigate, useNavigate } from 'react-router-dom';
import { HiBuildingOffice2 } from 'react-icons/hi2';
import { usePageTitle } from '../hooks/usePageTitle';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import { OrganizationsContent } from '../components/admin';
import { useCanManageOrganizations } from '../hooks/useCanManageOrganizations';

/**
 * Organizations management page — dedicated route for the multi-tenant
 * CRUD surface. Reached from the `VendorOrgSelector` "Manage organizations…"
 * affordance (ADR-114 Phase 3).
 *
 * Permission-gated by `useCanManageOrganizations` (vendor users + Django
 * staff). Unauthorized access redirects back to the Account hub.
 */
export function Organizations() {
  const { t } = useTranslation();
  usePageTitle(t('org.title', 'Organizations'));
  const navigate = useNavigate();
  const canManage = useCanManageOrganizations();

  // Defensive: a user who lost the permission mid-session shouldn't get
  // stuck on this page. Bounce them to the Account hub.
  useEffect(() => {
    if (!canManage) {
      navigate('/account', { replace: true });
    }
  }, [canManage, navigate]);

  if (!canManage) {
    // First render before the effect fires — render nothing rather than
    // a flash of the gated content.
    return <Navigate to="/account" replace />;
  }

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={HiBuildingOffice2}
        title={t('org.title', 'Organizations')}
        subtitle={t('org.subtitle', 'Manage tenant organizations')}
        showSearch={true}
        showNotifications={true}
      />
      <OrganizationsContent />
    </Box>
  );
}
